/* ============================================================
   PEMILIH GROUP  (/workspace-v3.9)  — SESAAT Studio v3.9
   ============================================================
   Group ialah rak produk di dalam Workspace. Ia BUKAN sekadar
   penapis senarai: produk baharu yang ditambah melalui composer
   mendarat ke dalam group yang sedang dipilih. Sebab itu pemilih
   ini duduk di KEPALA halaman — konteks untuk kedua-dua lajur —
   bukan di atas senarai bersama tab Generate/Flow/Schedule.

   Bentuknya diambil daripada menu chip composer (`.s-chip` +
   `.s-pop` + `.s-pop__row`), jadi tiada bentuk baharu masuk ke
   halaman ini.

   Beza yang DISENGAJAKAN daripada dropdown workspace lama:
     · pensel dan tong sampah muncul bila baris dituding sahaja.
       Versi lama membiarkan dua ikon itu terpampang pada setiap
       baris; dengan dua puluh group ia jadi empat puluh ikon
       bersaing dengan nama yang sepatutnya dibaca.
     · ada medan carian sebaik group melebihi lima.
     · padam membuka tetingkap pengesahan yang MENYEBUT berapa
       produk berpindah ke mana. Versi lama bertanya melalui dua
       kotak `confirm` browser berturut-turut — pengguna membaca
       yang pertama, menekan OK dua kali, dan tidak pernah tahu
       produknya pergi ke mana.
     · "New group" duduk di kepala menu yang TIDAK menatal.
       Ini keputusan sedia ada dalam versi lama (dahulu ia di
       hujung senarai dan pengguna dengan ~20 group langsung tidak
       dapat mencapainya) — dikekalkan di sini.

   ⚠️ PROTOTAIP — semua operasi menukar state contoh dalam
      WorkspaceV39View sahaja. Tiada apa-apa ditulis ke Firestore,
      dan TIADA isyarat dihantar ke extension.

   ⚠️ BILA MENYAMBUNG KE DATA SEBENAR nanti — otak group JANGAN
      ditulis semula di sini. Ia sudah wujud dan hidup dalam
      WorkspaceView + App.jsx; kongsikannya. Yang WAJIB terbawa:
        · "Main Group" hanyalah nama PAPARAN; nilai simpanan
          sebenar ialah `Workspace`, dan `all` bermaksud group
          utama itu. Nilai ini tertulis dalam puluhan ribu dokumen
          produk — menukarnya bermakna migrasi data.
        · tukar group menghantar isyarat SESAAT_GROUP_SYNC supaya
          panel extension mengikut group yang sama. Tanpa itu
          extension menjalankan Flow pada senarai yang salah.
        · group kosong mesti kekal selepas refresh (ia disimpan
          berasingan daripada produk).
        · padam group TIDAK memadam produk — produk berpindah.
   ============================================================ */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Check, ChevronDown, Folder, LayoutGrid, Pencil, Plus, Search, Trash2, X,
} from 'lucide-react';

import { Btn, Field } from '../design/ui';
import { useT } from '../i18n';
import PilihV39 from './PilihV39';

/* Group utama — tidak boleh dinamakan semula atau dipadam. */
export const GROUP_UTAMA = 'main';

/* Di bawah angka ini senarai cukup pendek untuk diimbas mata, jadi
   medan carian hanya menambah satu baris yang tidak berguna. */
const CARIAN_MUNCUL_BILA = 6;

/* ---------- Satu baris group dalam menu ------------------------------ */

function BarisGroup({ g, dipilih, onPilih, onSunting, onPadam }) {
    const t = useT();
    const Ikon = g.id === GROUP_UTAMA ? LayoutGrid : Folder;
    return (
        <div
            className="s-pop__row"
            role="menuitem"
            tabIndex={0}
            onClick={onPilih}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPilih(); }
            }}
        >
            <Ikon size={14} strokeWidth={1.8} style={{ flex: 'none', color: 'var(--s-ink-3)' }} />
            <span style={{
                flex: 1, minWidth: 0, fontSize: 13, color: 'var(--s-ink)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
                {g.nama}
            </span>
            <span className="s-num" style={{ flex: 'none' }}>{g.kira}</span>

            {/* Ruang kedua-dua ikon ini DIKEKALKAN walaupun ia lut sinar —
                kalau tidak, nama group melompat setiap kali tetikus lalu. */}
            {onSunting && (
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={t('workspace.group.renameAria', { nama: g.nama })}
                    title={t('workspace.group.renameTitle')}
                    className="s-pop__edit"
                    onClick={(e) => { e.stopPropagation(); onSunting(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onSunting(); } }}
                >
                    <Pencil size={12} strokeWidth={2} />
                </span>
            )}
            {onPadam && (
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={t('workspace.group.padamAria', { nama: g.nama })}
                    title={t('workspace.group.padamTitle')}
                    className="s-pop__edit"
                    onClick={(e) => { e.stopPropagation(); onPadam(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onPadam(); } }}
                >
                    <Trash2 size={12} strokeWidth={2} />
                </span>
            )}

            {dipilih && <Check size={14} strokeWidth={2.2} style={{ color: 'var(--s-ink)', flex: 'none' }} />}
        </div>
    );
}

/* ---------- Medan nama (cipta / namakan semula) ---------------------- */

/* ⛔ Esc SENGAJA tidak dikendalikan di sini — ia milik pendengar menu
   sahaja (lihat useEffect di bawah). Dua pengendali untuk kekunci yang
   sama merosakkan menu: pembatalan di sini menukar state, pendengar
   menu berdaftar semula serta-merta, dan ketukan Esc yang SAMA itu
   kemudian menutup seluruh menu. Diuji dan disahkan berlaku. */
function MedanNama({ nilai, onUbah, onSimpan, placeholder }) {
    const t = useT();
    return (
        <div style={{ display: 'flex', gap: 6, padding: '2px 4px' }}>
            <input
                autoFocus
                className="s-input"
                value={nilai}
                placeholder={placeholder}
                onChange={(e) => onUbah(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onSimpan(); } }}
            />
            <Btn size="sm" style={{ flex: 'none' }} onClick={onSimpan}>{t('workspace.group.simpan')}</Btn>
        </div>
    );
}

/* ---------- Pemilih ---------------------------------------------------
   groups : [{ id, nama, kira }] — group utama dahulu
   aktif  : id group yang sedang dibuka                                */

export default function GroupSwitcherV39({
    groups = [], aktif, onPilih, onCipta, onNamaSemula, onMintaPadam,
}) {
    const t = useT();
    const [buka, setBuka] = useState(false);
    const [cari, setCari] = useState('');
    const [modBaharu, setModBaharu] = useState(false);
    const [namaBaharu, setNamaBaharu] = useState('');
    const [suntingId, setSuntingId] = useState(null);
    const [namaSunting, setNamaSunting] = useState('');
    const [ralat, setRalat] = useState('');
    const rujuk = useRef(null);

    const pilihan = groups.find((g) => g.id === aktif) || groups[0];

    const tutup = () => {
        setBuka(false);
        setCari('');
        setModBaharu(false);
        setNamaBaharu('');
        setSuntingId(null);
        setNamaSunting('');
        setRalat('');
    };

    /* Menu ditutup bila diklik di luar atau Esc ditekan — tanpa ini ia
       tersangkut terbuka dan menutup kepala halaman.

       Esc membatalkan medan nama yang terbuka DAHULU, barulah menu.
       Tanpa tingkat ini, satu ketukan Esc semasa menaip nama membuang
       taipan DAN menutup menu sekali — pengguna terpaksa mula semula
       dari butang. Corak sama seperti modal produk (Esc menutup petak
       yang sedang diedit dahulu, barulah modal). */
    useEffect(() => {
        if (!buka) return undefined;
        const luar = (e) => { if (rujuk.current && !rujuk.current.contains(e.target)) tutup(); };
        const esc = (e) => {
            if (e.key !== 'Escape') return;
            if (modBaharu) { setModBaharu(false); setNamaBaharu(''); setRalat(''); return; }
            if (suntingId) { setSuntingId(null); setNamaSunting(''); setRalat(''); return; }
            tutup();
        };
        document.addEventListener('mousedown', luar);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', luar);
            document.removeEventListener('keydown', esc);
        };
    }, [buka, modBaharu, suntingId]);

    /* Nama disemak SEBELUM disimpan, bukan selepas. Dua group bernama
       sama bermakna pengguna tidak dapat membezakannya di mana-mana
       dropdown lain (Move Group, destinasi padam). */
    const semakNama = (nama, kecualiId) => {
        const bersih = (nama || '').trim();
        if (!bersih) return t('workspace.group.ralatKosong');
        /* ⛔ 'main group' di bawah ialah nilai PERBANDINGAN, bukan ayat UI —
           ia nama simpanan group utama. Jangan terjemahkannya. */
        if (bersih.toLowerCase() === 'main group') return t('workspace.group.ralatNamaTerpelihara');
        const bertindih = groups.some(
            (g) => g.id !== kecualiId && g.nama.trim().toLowerCase() === bersih.toLowerCase(),
        );
        if (bertindih) return t('workspace.group.ralatNamaBertindih');
        return '';
    };

    const cipta = () => {
        const salah = semakNama(namaBaharu, null);
        if (salah) { setRalat(salah); return; }
        onCipta?.(namaBaharu.trim());
        tutup();
    };

    const simpanNama = () => {
        const salah = semakNama(namaSunting, suntingId);
        if (salah) { setRalat(salah); return; }
        onNamaSemula?.(suntingId, namaSunting.trim());
        setSuntingId(null);
        setNamaSunting('');
        setRalat('');
    };

    const senarai = useMemo(() => {
        const q = cari.trim().toLowerCase();
        return q ? groups.filter((g) => g.nama.toLowerCase().includes(q)) : groups;
    }, [groups, cari]);

    return (
        <span ref={rujuk} style={{ position: 'relative', display: 'inline-flex' }}>
            <button
                type="button"
                className={`s-chip${buka ? ' s-chip--on' : ''}`}
                aria-haspopup="menu"
                aria-expanded={buka}
                title={t('workspace.group.tukarTitle')}
                onClick={() => (buka ? tutup() : setBuka(true))}
                /* ⛔ JANGAN tampal fontWeight / letterSpacing / muka taip di sini.
                   Dahulu tiga tampalan itu wujud kerana butang ini duduk DALAM
                   <h1> kepala halaman dan mewarisi gaya tajuk. Ia kini dihantar
                   melalui prop `sisiTajuk` PageHeader — di luar tajuk — jadi ia
                   mewarisi tetapan halaman yang sama seperti setiap butang lain.
                   Menampal semula bermakna butang ini terlepas daripada tema
                   bila tema berubah. */
            >
                <Folder size={14} strokeWidth={1.9} />
                <span style={{ maxWidth: 190 }}>{pilihan?.nama || t('workspace.group.utama')}</span>
                <span className="s-num" style={{ flex: 'none' }}>{pilihan?.kira ?? 0}</span>
                <ChevronDown size={13} strokeWidth={2} style={{ opacity: .6, flex: 'none' }} />
            </button>

            {buka && (
                <div
                    className="s-pop"
                    role="menu"
                    /* `.s-pop` menatal seluruh isinya. Di sini kepala menu
                       dikeluarkan daripada kawasan tatal supaya "New group"
                       dan carian kekal di tempatnya walau senarai panjang. */
                    style={{ width: 300, maxHeight: 'none', overflow: 'visible', padding: 0 }}
                >
                    <div style={{
                        padding: 5, borderBottom: '1px solid var(--s-line)',
                        display: 'flex', flexDirection: 'column', gap: 5,
                    }}>
                        {modBaharu ? (
                            <MedanNama
                                nilai={namaBaharu}
                                placeholder={t('workspace.group.namaBaharuPlaceholder')}
                                onUbah={(v) => { setNamaBaharu(v); setRalat(''); }}
                                onSimpan={cipta}
                            />
                        ) : (
                            <div
                                className="s-pop__row"
                                role="menuitem"
                                tabIndex={0}
                                onClick={() => { setModBaharu(true); setRalat(''); }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault(); setModBaharu(true); setRalat('');
                                    }
                                }}
                            >
                                <Plus size={14} strokeWidth={2} style={{ flex: 'none', color: 'var(--s-ink-3)' }} />
                                <span style={{ flex: 1, fontSize: 13, color: 'var(--s-ink)' }}>{t('workspace.group.baharu')}</span>
                            </div>
                        )}

                        {!modBaharu && groups.length >= CARIAN_MUNCUL_BILA && (
                            <Field
                                icon={<Search size={14} strokeWidth={1.9} />}
                                placeholder={t('workspace.group.cariPlaceholder')}
                                value={cari}
                                onChange={(e) => setCari(e.target.value)}
                                style={{ minWidth: 0, margin: '0 4px 2px' }}
                            />
                        )}

                        {ralat && (
                            <p className="s-hint" style={{ color: 'var(--s-stop)', margin: '0 6px 4px' }}>
                                {ralat}
                            </p>
                        )}
                    </div>

                    <div style={{ maxHeight: 264, overflowY: 'auto', padding: 5 }}>
                        {senarai.length === 0 && (
                            <p className="s-hint" style={{ margin: 0, padding: '6px 9px' }}>
                                {t('workspace.group.tiadaPadanan')}
                            </p>
                        )}

                        {senarai.map((g) => (
                            suntingId === g.id ? (
                                <MedanNama
                                    key={g.id}
                                    nilai={namaSunting}
                                    placeholder={t('workspace.group.namaPlaceholder')}
                                    onUbah={(v) => { setNamaSunting(v); setRalat(''); }}
                                    onSimpan={simpanNama}
                                />
                            ) : (
                                <BarisGroup
                                    key={g.id}
                                    g={g}
                                    dipilih={g.id === aktif}
                                    onPilih={() => { onPilih?.(g.id); tutup(); }}
                                    onSunting={g.id === GROUP_UTAMA ? null : () => {
                                        setSuntingId(g.id); setNamaSunting(g.nama); setRalat('');
                                    }}
                                    /* Menu ditutup dahulu: tetingkap pengesahan ialah
                                       lapisan penuh skrin, dan menu yang masih terbuka
                                       di belakangnya akan menutup dirinya sebaik
                                       tetingkap itu diklik. */
                                    onPadam={g.id === GROUP_UTAMA ? null : () => {
                                        tutup(); onMintaPadam?.(g.id);
                                    }}
                                />
                            )
                        ))}
                    </div>
                </div>
            )}
        </span>
    );
}

/* ---------- Tetingkap pengesahan padam --------------------------------
   Dirender oleh HALAMAN, bukan oleh pemilih di atas — pemilih itu duduk
   di dalam <h1> kepala halaman, dan lapisan penuh skrin tidak boleh
   hidup di dalam tajuk.

   Ayatnya menyebut dua perkara yang `confirm` browser lama tidak
   pernah jelaskan: produk TIDAK dipadam, dan produk itu pergi ke mana. */

export function PadamGroupModalV39({ g, groups = [], onBatal, onSah }) {
    const t = useT();
    const destinasi = groups.filter((x) => x.id !== g.id);
    const [dest, setDest] = useState(destinasi[0]?.id || GROUP_UTAMA);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onBatal(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onBatal]);

    const namaDest = groups.find((x) => x.id === dest)?.nama || t('workspace.group.utama');

    /* Ayat amaran ialah SATU kunci kamus, tetapi kiraan produk di dalamnya
       dicetak tebal. Susunan perkataan berbeza antara bahasa, jadi kedudukan
       kiraan itu tidak boleh diteka dari sini — sebaliknya kiraan dihantar
       masuk bersama penanda tak-boleh-taip, dan ayat yang SUDAH diterjemah
       dipecah semula pada penanda itu. Ayatnya kekal utuh dan satu; hanya
       satu potongan daripadanya dibalut <b>. */
    const PENANDA = '\u0000';
    const amaran = t('workspace.group.padamAmaran', { n: `${PENANDA}${g.kira}${PENANDA}` })
        .split(PENANDA);

    return (
        <div
            className="s-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t('workspace.group.padamAria', { nama: g.nama })}
            onClick={onBatal}
        >
            <div
                className="s-modal__panel"
                style={{ maxWidth: 440 }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className="s-modal__head">
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--s-ink)' }}>{t('workspace.group.padamTajuk')}</div>
                        <div className="s-dim" style={{
                            marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {g.nama}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="s-modal__x"
                        onClick={onBatal}
                        aria-label={t('workspace.group.tutupAria')}
                        title={t('workspace.group.tutupTitle')}
                    >
                        <X size={16} strokeWidth={2} />
                    </button>
                </header>

                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: 'var(--s-ink-2)' }}>
                        {g.kira === 0 ? (
                            t('workspace.group.padamKosong')
                        ) : (
                            <>
                                {amaran[0]}
                                <b style={{ color: 'var(--s-ink)' }}>{amaran[1]}</b>
                                {amaran[2]}
                            </>
                        )}
                    </p>

                    {g.kira > 0 && (
                        <label style={{ display: 'block' }}>
                            <span className="s-lbl">{t('workspace.group.pindahKe')}</span>
                            <PilihV39
                                ariaLabel={t('workspace.group.pindahKe')}
                                nilai={dest}
                                onPilih={setDest}
                                pilihan={destinasi.map((x) => ({ id: x.id, label: x.nama }))}
                            />
                        </label>
                    )}
                </div>

                <footer className="s-modal__foot">
                    <span className="s-dim" style={{ flex: 1, minWidth: 0 }}>
                        {g.kira > 0 ? t('workspace.group.ringkasanPindah', { n: g.kira, group: namaDest }) : ''}
                    </span>
                    <Btn variant="quiet" size="sm" onClick={onBatal}>{t('workspace.group.batal')}</Btn>
                    <Btn
                        size="sm"
                        onClick={() => onSah(dest)}
                        icon={<Trash2 size={14} strokeWidth={2} />}
                        /* Satu-satunya warna pada halaman ini. Ia dibenarkan di
                           sini atas sebab yang sama seperti "Delete" dalam bar
                           pukal: tindakan yang tidak boleh dibatalkan. */
                        style={{ background: 'var(--s-stop)', borderColor: 'var(--s-stop)', color: '#1A1A1A' }}
                    >
                        {t('workspace.group.padamButang')}
                    </Btn>
                </footer>
            </div>
        </div>
    );
}
